// src/pages/SettingsPage.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';

import api from '../api/axios';
import { defaultSettings, loadSettings, saveSettings } from '../utils/posSettings';
import { SUPPORTED_CURRENCIES } from '../utils/currency';

const initialWooForm = {
  url: '',
  currency: '',
  key: '',
  secret: '',
  hasKey: false,
  hasSecret: false,
  clearKey: false,
  clearSecret: false,
  source: 'none',
};

export default function SettingsPage() {
  const [settings, setSettings] = useState(defaultSettings);

  const [wooStatus, setWooStatus] = useState(null);
  const [wooForm, setWooForm] = useState(initialWooForm);
  const [wooErrors, setWooErrors] = useState({});
  const [wooMessage, setWooMessage] = useState('');
  const [loadingWoo, setLoadingWoo] = useState(true);
  const [wooSaving, setWooSaving] = useState(false);
  const [wooTesting, setWooTesting] = useState(false);
  const [syncingWoo, setSyncingWoo] = useState(false);

  const fetchWooData = useCallback(async () => {
    setLoadingWoo(true);
    try {
      const [statusRes, credentialRes] = await Promise.all([
        api.get('/integrations/woocommerce/status'),
        api.get('/integrations/woocommerce/credentials'),
      ]);

      setWooStatus(statusRes.data);
      setWooForm((prev) => ({
        ...prev,
        url: credentialRes.data.url || '',
        currency: credentialRes.data.currency || '',
        hasKey: credentialRes.data.hasKey,
        hasSecret: credentialRes.data.hasSecret,
        clearKey: false,
        clearSecret: false,
        key: '',
        secret: '',
        source: credentialRes.data.source || 'none',
      }));
      setWooErrors({});
      setWooMessage('');
    } catch (err) {
      setWooStatus(null);
      setWooMessage(err.response?.data?.error || err.message);
    } finally {
      setLoadingWoo(false);
    }
  }, []);

  useEffect(() => {
    setSettings(loadSettings());
    fetchWooData();
  }, [fetchWooData]);

  const handleSettingToggle = (key) => {
    const updated = { ...settings, [key]: !settings[key] };
    setSettings(updated);
    saveSettings(updated);
  };

  const handleCurrencyChange = (event) => {
    const updated = { ...settings, currencyCode: event.target.value };
    setSettings(updated);
    saveSettings(updated);
  };

  const handleWooFieldChange = (field) => (event) => {
    const value = event.target.value;
    setWooForm((prev) => ({
      ...prev,
      [field]: value,
    }));
    setWooErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleWooToggle = (field) => (event) => {
    const checked = event.target.checked;
    setWooForm((prev) => ({
      ...prev,
      [field]: checked,
      ...(field === 'clearKey' && checked ? { key: '' } : {}),
      ...(field === 'clearSecret' && checked ? { secret: '' } : {}),
    }));
    setWooErrors((prev) => ({ ...prev, key: '', secret: '' }));
  };

  const validateWooForm = () => {
    const errors = {};
    const trimmedUrl = wooForm.url.trim();
    if (!trimmedUrl) {
      errors.url = 'Store URL is required.';
    } else if (!/^https?:\/\//i.test(trimmedUrl)) {
      errors.url = 'Include http:// or https:// in the store URL.';
    }

    const keyInput = wooForm.key.trim();
    const secretInput = wooForm.secret.trim();

    const finalKey = wooForm.clearKey
      ? ''
      : keyInput || (wooForm.hasKey ? '__stored__' : '');
    const finalSecret = wooForm.clearSecret
      ? ''
      : secretInput || (wooForm.hasSecret ? '__stored__' : '');

    const disablingIntegration = finalKey === '' && finalSecret === '';

    if (!disablingIntegration) {
      if (finalKey === '') {
        errors.key = 'API key is required.';
      }
      if (finalSecret === '') {
        errors.secret = 'API secret is required.';
      }
    } else {
      // Ensure both toggles were explicitly chosen when disabling.
      if (!wooForm.clearKey || !wooForm.clearSecret) {
        errors.key = 'Clear both the key and secret to disable the integration.';
        errors.secret = errors.key;
      }
    }

    return errors;
  };

  const handleWooSave = async (event) => {
    event.preventDefault();
    const nextErrors = validateWooForm();
    if (Object.keys(nextErrors).length > 0) {
      setWooErrors(nextErrors);
      return;
    }

    const payload = {
      url: wooForm.url.trim(),
      currency: wooForm.currency.trim(),
    };

    if (wooForm.key.trim()) {
      payload.key = wooForm.key.trim();
    } else if (wooForm.clearKey) {
      payload.clearKey = true;
    }

    if (wooForm.secret.trim()) {
      payload.secret = wooForm.secret.trim();
    } else if (wooForm.clearSecret) {
      payload.clearSecret = true;
    }

    setWooSaving(true);
    setWooMessage('');
    try {
      const { data } = await api.put('/integrations/woocommerce/credentials', payload);
      toast.success(data.message || 'WooCommerce credentials saved.');

      setWooForm((prev) => ({
        ...prev,
        key: '',
        secret: '',
        clearKey: false,
        clearSecret: false,
        hasKey: data.credentials?.hasKey ?? prev.hasKey,
        hasSecret: data.credentials?.hasSecret ?? prev.hasSecret,
        currency: data.credentials?.currency ?? prev.currency,
        url: data.credentials?.url ?? prev.url,
      }));
      setWooStatus(data.status);
      setWooErrors({});
    } catch (err) {
      const message = err.response?.data?.error || err.message;
      setWooMessage(message);
      toast.error(message);
    } finally {
      setWooSaving(false);
      fetchWooData();
    }
  };

  const handleWooTest = async () => {
    setWooTesting(true);
    setWooMessage('');
    try {
      const payload = {
        url: wooForm.url.trim(),
      };
      if (wooForm.key.trim()) {
        payload.key = wooForm.key.trim();
      }
      if (wooForm.secret.trim()) {
        payload.secret = wooForm.secret.trim();
      }
      await api.post('/integrations/woocommerce/test', payload);
      toast.success('WooCommerce connection successful.');
    } catch (err) {
      const message = err.response?.data?.error || err.message;
      setWooMessage(message);
      toast.error(message);
    } finally {
      setWooTesting(false);
    }
  };

  const handleWooSync = async () => {
    setSyncingWoo(true);
    setWooMessage('');
    try {
      const { data } = await api.post('/integrations/woocommerce/sync');
      const summary = [
        `Matched ${data.matched ?? 0}`,
        data.created ? `Created ${data.created}` : null,
        data.unmatched?.length ? `${data.unmatched.length} unmatched` : null,
        data.skippedNoSku?.length ? `${data.skippedNoSku.length} without SKU` : null,
        `of ${data.totalProducts ?? 0}`,
      ]
        .filter(Boolean)
        .join(' | ');
      toast.success(`Sync complete. ${summary}`);
      await fetchWooData();
    } catch (err) {
      const message = err.response?.data?.error || err.message;
      setWooMessage(message);
      toast.error(message);
    } finally {
      setSyncingWoo(false);
    }
  };

  const statusChip = useMemo(() => {
    if (!wooStatus) {
      return {
        label: 'Unknown',
        className:
          'bg-amber-500/20 text-amber-200 border border-amber-500/40',
      };
    }
    if (wooStatus.configured) {
      return {
        label: 'Connected',
        className:
          'bg-emerald-500/15 text-emerald-300 border border-emerald-500/40',
      };
    }
    return {
      label: 'Not connected',
      className:
        'bg-rose-500/15 text-rose-200 border border-rose-500/40',
    };
  }, [wooStatus]);

  return (
    <div className="flex h-[calc(100vh-4rem)] flex-col gap-6 overflow-auto bg-slate-950 px-6 py-6 text-slate-100">
      <h1 className="text-2xl font-semibold tracking-tight text-slate-100">POS Settings</h1>

      <div className="grid gap-6 lg:grid-cols-[1.2fr,1.8fr]">
        <section className="rounded-3xl border border-slate-800 bg-slate-900/60 p-6 shadow-[0_25px_60px_-30px_rgba(8,15,26,0.9)]">
          <header className="mb-6 space-y-1">
            <h2 className="text-lg font-semibold text-slate-100">Till Preferences</h2>
            <p className="text-sm text-slate-400">
              Configure defaults that apply instantly to the sales screen.
            </p>
          </header>

          <div className="space-y-5">
            <label className="block space-y-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                Display Currency
              </div>
              <select
                value={settings.currencyCode}
                onChange={handleCurrencyChange}
                className="w-full rounded-2xl border border-slate-800 bg-slate-950/70 px-4 py-3 text-sm text-slate-100 focus:border-brandGreen/50 focus:outline-none focus:ring-2 focus:ring-brandGreen/30"
              >
                {SUPPORTED_CURRENCIES.map((option) => (
                  <option key={option.code} value={option.code}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <ToggleRow
              label="Enable 10% Service Charge"
              description="Adds service charge to new sales automatically."
              checked={settings.serviceChargeEnabled}
              onChange={() => handleSettingToggle('serviceChargeEnabled')}
            />

            <ToggleRow
              label="Enable Gratuity Input"
              description="Show gratuity prompt before completing payment."
              checked={settings.gratuityEnabled}
              onChange={() => handleSettingToggle('gratuityEnabled')}
            />

            <ToggleRow
              label="Enable Tax Exemption Option"
              description="Allow staff to mark a sale as tax exempt."
              checked={settings.taxExemptionEnabled}
              onChange={() => handleSettingToggle('taxExemptionEnabled')}
            />

            <div className="rounded-2xl border border-slate-800 bg-slate-950/50 px-4 py-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                Receipt delivery defaults
              </p>
              <p className="mt-1 text-xs text-slate-500">
                Choose which channels are preselected when the receipt modal opens.
              </p>
              <div className="mt-4 space-y-3">
                <ToggleRow
                  label="Ticket printer enabled by default"
                  description="Opens the receipt modal with Ticket Printer toggled on."
                  checked={settings.receiptPrintDefault}
                  onChange={() => handleSettingToggle('receiptPrintDefault')}
                />
                <ToggleRow
                  label="Email receipt enabled by default"
                  description="Starts with Email Receipt toggled on."
                  checked={settings.receiptEmailDefault}
                  onChange={() => handleSettingToggle('receiptEmailDefault')}
                />
                <ToggleRow
                  label="Prefill last customer details"
                  description="When enabled, the next receipt modal reuses the previous customer."
                  checked={settings.rememberCustomerDefault}
                  onChange={() => handleSettingToggle('rememberCustomerDefault')}
                />
              </div>
            </div>

            <p className="rounded-2xl border border-slate-800 bg-slate-950/60 px-4 py-3 text-xs text-slate-500">
              Settings are stored locally on this device and applied instantly across the till UI.
            </p>
          </div>
        </section>

        <section className="rounded-3xl border border-slate-800 bg-slate-900/60 p-6 shadow-[0_25px_60px_-30px_rgba(8,15,26,0.9)]">
          <header className="mb-6 space-y-1">
            <h2 className="text-lg font-semibold text-slate-100">WooCommerce Integration</h2>
            <p className="text-sm text-slate-400">
              Manage credentials and keep online pricing in sync with the POS.
            </p>
          </header>

          <div className="space-y-4">
            <div className="rounded-2xl border border-slate-800 bg-slate-950/60 px-4 py-3">
              <div className="flex items-center justify-between">
                <span
                  className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${statusChip.className}`}
                >
                  {statusChip.label}
                </span>
                <span className="text-[11px] uppercase tracking-wide text-slate-500">
                  {wooStatus?.lastSync
                    ? `Last sync ${new Date(wooStatus.lastSync).toLocaleString()}`
                    : 'No sync recorded'}
                </span>
              </div>
              <dl className="mt-4 space-y-1 text-xs text-slate-400">
                <div className="flex justify-between gap-3">
                  <dt className="text-slate-500">Site URL</dt>
                  <dd className="text-slate-200">
                    {wooStatus?.url ? wooStatus.url : 'Not set'}
                  </dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-slate-500">API key stored</dt>
                  <dd className="text-slate-200">{wooStatus?.hasKey ? 'Yes' : 'No'}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-slate-500">API secret stored</dt>
                  <dd className="text-slate-200">{wooStatus?.hasSecret ? 'Yes' : 'No'}</dd>
                </div>
              </dl>
            </div>

            <form onSubmit={handleWooSave} className="space-y-4">
              <div className="space-y-2">
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                  Store URL
                </label>
                <input
                  type="url"
                  value={wooForm.url}
                  onChange={handleWooFieldChange('url')}
                  className={`w-full rounded-2xl border px-4 py-3 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-brandGreen/30 ${
                    wooErrors.url
                      ? 'border-rose-500/60 bg-rose-950/40 placeholder:text-rose-200/60'
                      : 'border-slate-800 bg-slate-950/70 placeholder:text-slate-500'
                  }`}
                  placeholder="https://store.example.com"
                />
                {wooErrors.url && <p className="text-xs text-rose-300">{wooErrors.url}</p>}
              </div>

              <div className="space-y-2">
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                  Currency (optional)
                </label>
                <input
                  type="text"
                  value={wooForm.currency}
                  onChange={handleWooFieldChange('currency')}
                  className="w-full rounded-2xl border border-slate-800 bg-slate-950/70 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-brandGreen/30"
                  placeholder="e.g. GBP"
                />
                <p className="text-xs text-slate-500">
                  Used when WooCommerce does not return a currency for price data.
                </p>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                    API key
                  </label>
                  <input
                    type="text"
                    value={wooForm.key}
                    onChange={handleWooFieldChange('key')}
                    disabled={wooForm.clearKey}
                    placeholder={wooForm.hasKey ? 'Leave blank to keep stored key' : 'ck_...'}
                    className={`w-full rounded-2xl border px-4 py-3 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-brandGreen/30 ${
                      wooErrors.key
                        ? 'border-rose-500/60 bg-rose-950/40 placeholder:text-rose-200/60'
                        : 'border-slate-800 bg-slate-950/70 placeholder:text-slate-500'
                    } ${wooForm.clearKey ? 'opacity-60' : ''}`}
                  />
                  <label className="inline-flex items-center gap-2 text-xs text-slate-400">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-slate-700 bg-slate-900 text-brandGreen focus:ring-brandGreen"
                      checked={wooForm.clearKey}
                      onChange={handleWooToggle('clearKey')}
                    />
                    Clear stored key
                  </label>
                  {wooErrors.key && <p className="text-xs text-rose-300">{wooErrors.key}</p>}
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                    API secret
                  </label>
                  <input
                    type="password"
                    value={wooForm.secret}
                    onChange={handleWooFieldChange('secret')}
                    disabled={wooForm.clearSecret}
                    placeholder={wooForm.hasSecret ? 'Leave blank to keep stored secret' : 'cs_...'}
                    className={`w-full rounded-2xl border px-4 py-3 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-brandGreen/30 ${
                      wooErrors.secret
                        ? 'border-rose-500/60 bg-rose-950/40 placeholder:text-rose-200/60'
                        : 'border-slate-800 bg-slate-950/70 placeholder:text-slate-500'
                    } ${wooForm.clearSecret ? 'opacity-60' : ''}`}
                  />
                  <label className="inline-flex items-center gap-2 text-xs text-slate-400">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-slate-700 bg-slate-900 text-brandGreen focus:ring-brandGreen"
                      checked={wooForm.clearSecret}
                      onChange={handleWooToggle('clearSecret')}
                    />
                    Clear stored secret
                  </label>
                  {wooErrors.secret && <p className="text-xs text-rose-300">{wooErrors.secret}</p>}
                </div>
              </div>

              {wooMessage && (
                <div className="rounded-2xl border border-rose-500/40 bg-rose-900/20 px-4 py-3 text-xs text-rose-200">
                  {wooMessage}
                </div>
              )}

              <div className="flex flex-wrap gap-3 pt-2">
                <button
                  type="submit"
                  disabled={wooSaving || loadingWoo}
                  className="inline-flex items-center justify-center rounded-full bg-brandGreen px-5 py-2 text-sm font-semibold uppercase tracking-wide text-white transition hover:bg-brandDark disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {wooSaving ? 'Saving...' : 'Save credentials'}
                </button>
                <button
                  type="button"
                  onClick={handleWooTest}
                  disabled={wooTesting || loadingWoo}
                  className="inline-flex items-center justify-center rounded-full border border-slate-700 px-5 py-2 text-sm font-semibold uppercase tracking-wide text-slate-200 transition hover:border-slate-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {wooTesting ? 'Testing...' : 'Test connection'}
                </button>
                <button
                  type="button"
                  onClick={handleWooSync}
                  disabled={syncingWoo || !wooStatus?.configured}
                  className="inline-flex items-center justify-center rounded-full border border-emerald-500/40 bg-emerald-500/10 px-5 py-2 text-sm font-semibold uppercase tracking-wide text-emerald-200 transition hover:bg-emerald-500/20 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {syncingWoo ? 'Syncing...' : 'Sync products'}
                </button>
              </div>
            </form>
          </div>
        </section>
      </div>
    </div>
  );
}

function ToggleRow({ label, description, checked, onChange }) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-2xl border border-slate-800 bg-slate-950/50 px-4 py-3">
      <div>
        <div className="text-sm font-medium text-slate-100">{label}</div>
        <div className="text-xs text-slate-500">{description}</div>
      </div>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="h-5 w-5 rounded border-slate-700 bg-slate-900 text-brandGreen focus:ring-brandGreen"
      />
    </div>
  );
}
